/* @flow */
import * as React from "react";
import Media from "react-media";
import idx from "idx";
import { withRouter, Redirect } from "react-router-dom";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import Orientation from "../common/Orientation";
import { ArtificialTourDeals } from "../TradeControls/TradeControls";
import TradeHistory from "../TradeControls/History";
import TradeSettings from "../TradeControls/Settings";
import { CastDown, CastUp } from "../TradeControls/Buttons";
import { DealsIcon, SettingsIcon } from "../TradeControls/Icons";
import { formatTimeInterval } from "../TradeModals/TradeIsClosing";
import { ActiveTournament } from "../Tournaments/Api";
import { ModalConsumer } from "../common/ModalProvider";
import { fontSize } from "../common/styled-mixins";
import OperationCheque from "../Trade/OperationCheque";
import FeatureFlags from "../../FeatureFlags";
import { gaReducer } from "../GA";
import LeftArrowIcon from "../Icons/Trade/LeftArrow";

import type { Location } from "react-router-dom";
import type { Dispatch } from "../../redux/action";
import type { Deal, StockPoint, Stock } from "../../redux/CommonTypes";
import type { State as ReduxState } from "../../redux/reducer";
import Tour, { TourContext } from "../Tour";
import Profit from "./Profit";
import ProtectionInfo from "../Protection/ProtectionInfo";

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-content: flex-start;
  align-items: flex-start;
`;

const Container = styled(Flex)`
  position: relative;
  width: 100%;
  height: auto;
  background-color: ${props => props.theme.mainLight};
  flex-grow: 1;
  flex-shrink: 1;

  @media (max-width: 479px) {
    display: block;
  }
`;

const Content = styled(Flex)`
  position: relative;
  justify-content: space-between;
  align-content: space-between;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 82px;
  width: 100%;
  height: auto;
  overflow: hidden;

  @media (max-width: 479px) {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    height: auto;
    overflow: hidden;
  }
`;

const DealsInner = styled.div`
  position: absolute;
  top: 0;
  right: 40px;
  width: calc(50% - 80px);
  height: 100%;
  overflow: auto;
`;

const DealsContainer = styled.div`
  width: 100%;
  overflow: auto;

  @media (max-width: 479px) {
    display: flex;
    flex-direction: column;
    flex: 0;
  }
`;

const TabsContainer = styled(Flex)`
  flex-direction: row;
  flex-grow: 0;
  flex-shrink: 1;
  flex-basis: 45px;
  width: 100%;
  min-height: 45px;

  @media (min-width: 480px) {
    flex-basis: 38px;
    min-height: 38px;
  }
`;

const TabView = styled(Flex)`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 50%;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  align-items: center;
  font-weight: 300;
  font-size: ${fontSize(14)};
  height: 45px;
  text-transform: uppercase;
  color: #99959d;
  background-color: ${props => props.theme.mainDark};
  cursor: pointer;

  @media (min-width: 480px) {
    justify-content: center;
    height: 38px;
  }

  @media (min-width: 480px) and (max-height: 289px) {
    height: 38px;
  }

  ${props =>
    props.active &&
    css`
      color: ${({ theme }) => theme.foreground};
      background-color: ${props => props.theme.mainLight};
    `};
`;

const SvgContainer = styled.div`
  position: relative;
  align-self: center;

  @media (max-width: 479px) {
    position: absolute;
    top: 50%;
    left: 21px;
    transform: translateY(-50%);
  }
`;

const Title = styled.div`
  margin-left: 59px;

  @media (min-width: 480px) {
    display: none;
    margin-left: 0;
  }
`;

const SettingsContainer = styled(TradeSettings)`
  width: 100%;
  padding: 0 16px;

  @media (max-width: 479px) {
    padding: 10px 15px 0;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};
`;

const ButtonContainer = styled(Flex)`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  align-content: center;
  text-align: center;
  width: 100%;
  flex: 0 0 auto;
  padding: 8px 15px;

  @media (min-width: 480px) {
    justify-content: space-between;
    align-content: space-between;
    flex-basis: 92px;
    min-height: 92px;
    padding: 5px 16px;
  }

  ${props =>
    props.deals &&
    css`
      box-shadow: 0px -13px 26px 0px ${props => props.theme.mainLight};
      z-index: 2;
    `};

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};

  :before {
    ${({ tourBorder, isLandscape }) =>
      tourBorder &&
      css`
        position: absolute;
        left: ${isLandscape ? 0 : "10px"};
        top: ${isLandscape ? 0 : 0};
        right: 10px;
        bottom: 9px;
        border-radius: ${isLandscape ? "6px 0 0 0" : 0};
        ${tourBorder.border};
        content: "";
      `};

    ${({ tourBorderAnimation }) =>
      tourBorderAnimation &&
      tourBorderAnimation.animation(tourBorderAnimation.keyframe)};
  }
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 100%;
  align-self: stretch;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 959px) {
    margin: 0;
  }
  @media (max-width: 479px) {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 50%;
    width: 50%;
    align-self: center;

    :first-of-type {
      padding-right: 8px;
    }
    :last-of-type {
      padding-left: 8px;
    }
  }
`;

const ClockContainer = styled.div`
  position: relative;
  margin: auto;
  padding: 4px 16px;
  font-weight: 400;
  font-size: ${fontSize(12)};
  text-align: center;
  color: #99959d;

  @media (max-width: 479px) {
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
  }
`;

const Clock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: ${({ theme }) => theme.foreground};
  cursor: default;

  ${props =>
    props.timeOtc &&
    css`
      color: ${({ theme }) => theme.accent};
    `};
`;

const ClockIcon = styled.div`
  margin-right: 7px;
  font-size: 0;
  line-height: 0;
  color: inherit;
  opacity: 0.4;
`;

const ClockText = styled.span`
  @media (max-width: 359px) {
    padding-top: 0;
  }
`;

const SlideButtonContainer = styled.button`
  position: absolute;
  left: 5px;
  top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 59px;
  padding: 0;
  color: #b5b7be;
  background-color: transparent;
  border: none;
  z-index: 1;

  ${({ right }) =>
    right &&
    css`
      left: auto;
      right: 5px;
      transform: rotate(180deg);
    `};

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `};
`;

const ProfitContainer = styled.div`
  width: 100%;
  text-align: center;
  padding-bottom: ${({ showClock }) => (showClock ? 0 : "7px")};

  @media (max-width: 479px) {
    align-self: flex-end;
    padding-top: ${({ showClock }) => (showClock ? 0 : "7px")};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};
`;

// settings | deals
const AnimateContainer = styled.div`
  position: relative;
  display: flex;
  width: 200%;
  overflow: hidden;
  transition: transform 0.1s ease;
  ${({ tab }) =>
    tab &&
    css`
      transform: translateX(${tab === "settings" ? 0 : "-50%"});
    `};
`;

const SettingsAnimateContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 50%;
  min-width: 50%;
  margin: 0 auto;
  opacity: 1;
  ${({ showTab }) =>
    showTab === "deals" &&
    css`
      opacity: 0;
      transition: opacity 0.1s ease;
    `};
`;

const DealsAnimateContainer = styled.div`
  width: 50%;
  min-width: 50%;
  margin: 0 auto;
  opacity: 1;
  ${({ showTab }) =>
    showTab === "settings" &&
    css`
      opacity: 0;
      transition: opacity 0.1s ease;
    `};
`;

const SlideButton = ({ onClick, right, disabled }) => (
  <SlideButtonContainer right={right} onClick={onClick} disabled={disabled}>
    <LeftArrowIcon width="10px" />
  </SlideButtonContainer>
);

type RemoteTradeSettings = $PropertyType<ReduxState, "remoteTradeSettings">;
type Settings = $PropertyType<ReduxState, "settings">;

type TabProps = {
  active: boolean,
  title: React.Node,
  onClick: () => void,
  Icon: React.Node,
  forwardedRef?: React.Node
};

function Tab0(props: TabProps) {
  return (
    <TabView
      active={props.active}
      onClick={props.onClick}
      ref={props.forwardedRef}
    >
      <SvgContainer>{props.Icon}</SvgContainer>
      <Title>{props.title}</Title>
    </TabView>
  );
}

const Tab = React.forwardRef((props, ref) => (
  <Tab0 {...props} forwardedRef={ref} />
));

type Props = {
  tradeMode: "tournament" | "binary",
  showTournamentRefill: boolean,
  location: Location,
  activeAccount: Object,
  point: StockPoint,
  pointsByStock: Object | void,
  deals: $PropertyType<ReduxState, "deals">,
  percentBidThreshold: $PropertyType<
    RemoteTradeSettings,
    "percentBidThreshold"
  >,
  percentBids: $PropertyType<RemoteTradeSettings, "percentBids">,
  suggestedBids: $PropertyType<RemoteTradeSettings, "bids">,
  possibleIntervals: $PropertyType<RemoteTradeSettings, "intervals">,
  currentBid: $PropertyType<Settings, "currentBid">,
  currentInterval: $PropertyType<Settings, "currentInterval">,
  now: number,
  timestamp: number,
  maxBidVolume: number,
  isActiveAccountReal: boolean,
  accountType: "demo" | "real",
  isDealAmountValid: boolean,
  validateDealAmount: (key: boolean) => void,
  onSubmit: (deal: Deal) => void,
  onIntentUpdate: (nextIntent: any) => void,
  showIncreaseBidLimitMessage: () => void,
  dispatch: Dispatch,
  timeUntilNextLockToggle: number,
  stock: Stock,
  tradeMode: "tournament" | "binary"
};

type State = {
  activeTab: string
};

class TradeControlTabs extends React.Component<Props, State> {
  static contextType = TourContext;

  state = {
    repetitive: 0,
    specialAnimation: false,
    activeTab: "settings",
    tourInterval: 30
  };

  setActiveTabClick = (key: string) => {
    if (key !== this.state.activeTab) {
      key === "settings"
        ? gaReducer("CLICK_MOBILE_SETTINGS")
        : gaReducer("CLICK_MOBILE_DEALS");
    }

    this.setActiveTab(key);
  };

  setActiveTab = (key: string) => {
    if (key === this.state.activeTab) {
      return this.setState({
        repetitive: this.state.repetitive + 1,
        specialAnimation: true
      });
    }

    this.setState({
      activeTab: key,
      specialAnimation: false
    });
  };

  setNextTab = () => {
    this.state.activeTab === "settings"
      ? this.setState({
          activeTab: "deals",
          showTab: "deals",
          specialAnimation: false
        })
      : this.setState({
          activeTab: "settings",
          showTab: "settings",
          specialAnimation: false
        });
  };

  handleSettingsUpdate = (nextSettings: *) => {
    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: nextSettings
    });
  };

  _submit(type: "UP" | "DOWN") {
    const { currentBid, currentInterval, point, accountType } = this.props;

    type === "UP"
      ? gaReducer("CLICKBTN_UPPER_BET")
      : gaReducer("CLICKBTN_LOWER_BET");

    if (!point) {
      return; // Или может throw?
    }

    this.props.onSubmit({
      id: null,
      time: 1337,
      percent: null,
      bidType: type === "UP",
      amount: currentBid,
      bTime: currentInterval,
      stock: point.stock,
      value: point.value,
      accountType,
      timestamp: Number(point.date / 1000),
      open_timestamp: Number(point.date / 1000),
      date: point.date
    });
  }

  original = React.createRef(null);
  originalDeals = React.createRef(null);
  originalDealsTab = React.createRef(null);
  settingsRef = React.createRef(null);

  render() {
    const {
      activeAccount,
      deals,
      percentBids,
      suggestedBids,
      percentBidThreshold,
      possibleIntervals,
      currentBid,
      currentInterval,
      maxBidVolume,
      showIncreaseBidLimitMessage,
      accountType,
      tradeMode,
      showTournamentRefill,
      active_tournament,
      isDealAmountValid,
      validateDealAmount,
      disabled,
      dealsToCompensate,
      compensationUpdated,
      compensationCreated,
      onCompensationCreated
    } = this.props;
    const { tourInterval } = this.state;

    const id = idx(active_tournament, t => t.value.id);

    if (
      tradeMode === "tournament" &&
      id != null &&
      showTournamentRefill === true
    ) {
      const redirect = `/trade/tournaments/refill/${id}`;

      if (this.props.location.pathname !== redirect) {
        return <Redirect to={redirect} />;
      }
    }

    const TABS = [
      {
        key: "settings",
        title: <Gettext>Settings</Gettext>,
        Icon: <SettingsIcon />
      },
      {
        key: "deals",
        title: <Gettext>Deals</Gettext>,
        Icon: <DealsIcon />
      }
    ];

    // TODO: Показываем часы и красим их в жёлтый
    const showClock = this.props.timeUntilNextLockToggle < 1000 * 60 * 60 * 12;
    const timeOtc = this.props.stock.otc;

    return (
      <Media query="(max-width: 479px)">
        {matches =>
          matches ? (
            <Orientation>
              {screen => (
                <div>
                  {tradeMode === "binary" && (
                    <ProtectionInfo
                      isDesktop={false}
                      dealsToCompensate={dealsToCompensate}
                      compensationUpdated={compensationUpdated}
                      compensationCreated={compensationCreated}
                      onCompensationCreated={onCompensationCreated}
                      activeAccount={activeAccount}
                    />
                  )}
                  <Content>
                    <SlideButton
                      onClick={this.setNextTab}
                      disabled={this.state.activeTab === "settings"}
                    />
                    <SlideButton
                      onClick={this.setNextTab}
                      right
                      disabled={this.state.activeTab === "deals"}
                    />
                    <AnimateContainer tab={this.state.activeTab}>
                      <SettingsAnimateContainer
                        key="settings"
                        showTab={this.state.showTab}
                        ref={this.settingsRef}
                      >
                        <SettingsContainer
                          tradeMode={tradeMode}
                          activeAccount={activeAccount}
                          percentBids={percentBids}
                          suggestedBids={suggestedBids}
                          possibleIntervals={possibleIntervals}
                          percentBidThreshold={percentBidThreshold}
                          currentBid={currentBid}
                          currentInterval={currentInterval}
                          onSettingsUpdate={this.handleSettingsUpdate}
                          showIncreaseBidLimitMessage={
                            showIncreaseBidLimitMessage
                          }
                          isActiveAccountReal={accountType === "real"}
                          maxBidVolume={maxBidVolume}
                          isDealAmountValid={isDealAmountValid}
                          validateDealAmount={validateDealAmount}
                          tourInterval={tourInterval}
                          onTourIntervalChange={value => {
                            this.setState({ tourInterval: value });
                          }}
                          disabled={disabled}
                        >
                          {showClock && (
                            <ClockContainer>
                              <Clock timeOtc={timeOtc}>
                                <ClockIcon timeOtc={timeOtc}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="-5651.999 5637.688 15.997 20.001"
                                    width="16"
                                    height="20"
                                    fill="currentColor"
                                  >
                                    <path
                                      d="M1238-12037.673a7.953,7.953,0,0,1,2.35-5.649l-.9-1.264a.493.493,0,0,1,.118-.692l1.318-.932a.5.5,0,0,1,.7.118l.889,1.252a7.939,7.939,0,0,1,2.262-.717v-.514a.6.6,0,0,0-.6-.6h-.33a.6.6,0,0,1-.6-.6v-1.823a.6.6,0,0,1,.6-.6h4.385a.6.6,0,0,1,.6.6v1.823a.6.6,0,0,1-.6.6h-.33a.6.6,0,0,0-.6.6v.514a7.854,7.854,0,0,1,2.12.65l.9-1.268a.5.5,0,0,1,.7-.117l1.318.934a.5.5,0,0,1,.121.693l-.877,1.233a7.948,7.948,0,0,1,2.462,5.758,8,8,0,0,1-8,7.983A8,8,0,0,1,1238-12037.673Zm1.92,0a6.078,6.078,0,0,0,6.078,6.066,6.081,6.081,0,0,0,6.081-6.066,6.083,6.083,0,0,0-6.081-6.069A6.081,6.081,0,0,0,1239.922-12037.673Zm6.039-.042v-4.96l.039,0a5.016,5.016,0,0,1,5.014,4.963Z"
                                      transform="translate(-6890 17687.379)"
                                    />
                                  </svg>
                                </ClockIcon>
                                {formatTimeInterval(
                                  this.props.timeUntilNextLockToggle
                                )}
                              </Clock>
                              <Media
                                query="(max-width: 479px), (min-width: 640px)"
                                render={() => (
                                  <ClockText>
                                    <Gettext>
                                      left till the market closure
                                    </Gettext>
                                  </ClockText>
                                )}
                              />
                            </ClockContainer>
                          )}
                        </SettingsContainer>
                        <ProfitContainer showClock={showClock}>
                          <Profit percentage={90} />
                        </ProfitContainer>
                      </SettingsAnimateContainer>
                      <DealsAnimateContainer
                        key={`deals${this.state.repetitive}`}
                        showTab={this.state.showTab}
                      >
                        <DealsInner>
                          <DealsContainer>
                            <ModalConsumer>
                              {({ showModal }) => (
                                <TradeHistory
                                  tradeMode={tradeMode}
                                  mobile={true}
                                  deals={deals}
                                  now={this.props.now}
                                  point={this.props.point}
                                  pointsByStock={this.props.pointsByStock}
                                  onOperationClick={operation => {
                                    if (
                                      FeatureFlags.OperationCheque &&
                                      operation.profit != null
                                    ) {
                                      showModal(OperationCheque, {
                                        operation,
                                        type: tradeMode
                                      });
                                    } else {
                                      this.props.dispatch({
                                        type: "SWITCH_TO_STOCK",
                                        payload: operation.stock
                                      });
                                    }
                                  }}
                                  refundClickHandler={
                                    this.props.refundClickHandler
                                  }
                                  refundFetching={this.props.refundFetching}
                                />
                              )}
                            </ModalConsumer>
                          </DealsContainer>
                        </DealsInner>
                      </DealsAnimateContainer>
                    </AnimateContainer>
                    <Tour step="makeDeal" originalRef={this.original}>
                      {({ next, isOriginal, tour }) => (
                        <ButtonContainer
                          tourBorder={!isOriginal && tour && tour.borderStyles}
                          tourBorderAnimation={
                            !isOriginal &&
                            tour &&
                            tour.outside &&
                            tour.borderStyles
                          }
                          isLandscape={false}
                          deals={this.state.activeTab === "deals"}
                          ref={isOriginal ? this.original : null}
                          disabled={disabled}
                        >
                          <ButtonWrapper>
                            <CastUp
                              onClick={() => {
                                if (isOriginal) {
                                  this._submit("UP");
                                } else {
                                  next(["UP", currentBid, tourInterval]);
                                }
                              }}
                            />
                          </ButtonWrapper>
                          <ButtonWrapper>
                            <CastDown
                              onClick={() => {
                                if (isOriginal) {
                                  this._submit("DOWN");
                                } else {
                                  next(["DOWN", currentBid, tourInterval]);
                                }
                              }}
                            />
                          </ButtonWrapper>
                        </ButtonContainer>
                      )}
                    </Tour>
                  </Content>
                </div>
              )}
            </Orientation>
          ) : (
            <Orientation>
              {screen => (
                <Container>
                  <TabsContainer>
                    {TABS.map(tab => {
                      const { title, key, Icon } = tab;
                      return (
                        <Tour
                          step="chartWithDeals"
                          originalRef={this.originalDealsTab}
                          withoutStyles={true}
                          disabledTooltip={true}
                          dealsTab={true}
                        >
                          {({ next, isOriginal }) => (
                            <Tab
                              ref={isOriginal ? this.originalDealsTab : null}
                              title={title}
                              key={key}
                              active={
                                isOriginal
                                  ? key === this.state.activeTab
                                  : "deals"
                              }
                              Icon={Icon}
                              onClick={
                                isOriginal
                                  ? () => this.setActiveTabClick(key)
                                  : () => void 0
                              }
                            />
                          )}
                        </Tour>
                      );
                    })}
                  </TabsContainer>
                  <Content>
                    {tradeMode === "binary" && (
                      <ProtectionInfo
                        isDesktop={false}
                        isLandscape={true}
                        dealsToCompensate={dealsToCompensate}
                        compensationUpdated={compensationUpdated}
                        compensationCreated={compensationCreated}
                        onCompensationCreated={onCompensationCreated}
                        activeAccount={activeAccount}
                      />
                    )}
                    {this.state.activeTab === "settings" &&
                    (this.context == null ||
                      this.context.step !== "chartWithDeals") ? (
                      <SettingsContainer
                        tradeMode={tradeMode}
                        activeAccount={activeAccount}
                        percentBids={percentBids}
                        suggestedBids={suggestedBids}
                        possibleIntervals={possibleIntervals}
                        percentBidThreshold={percentBidThreshold}
                        currentBid={currentBid}
                        currentInterval={currentInterval}
                        onSettingsUpdate={this.handleSettingsUpdate}
                        showIncreaseBidLimitMessage={
                          showIncreaseBidLimitMessage
                        }
                        isActiveAccountReal={accountType === "real"}
                        maxBidVolume={maxBidVolume}
                        isDealAmountValid={isDealAmountValid}
                        validateDealAmount={validateDealAmount}
                        tourInterval={tourInterval}
                        onTourIntervalChange={value => {
                          this.setState({ tourInterval: value });
                        }}
                        disabled={disabled}
                      >
                        {showClock && (
                          <ClockContainer>
                            <Clock timeOtc={timeOtc}>
                              <ClockIcon timeOtc={timeOtc}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="-5651.999 5637.688 15.997 20.001"
                                  width="16"
                                  height="20"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M1238-12037.673a7.953,7.953,0,0,1,2.35-5.649l-.9-1.264a.493.493,0,0,1,.118-.692l1.318-.932a.5.5,0,0,1,.7.118l.889,1.252a7.939,7.939,0,0,1,2.262-.717v-.514a.6.6,0,0,0-.6-.6h-.33a.6.6,0,0,1-.6-.6v-1.823a.6.6,0,0,1,.6-.6h4.385a.6.6,0,0,1,.6.6v1.823a.6.6,0,0,1-.6.6h-.33a.6.6,0,0,0-.6.6v.514a7.854,7.854,0,0,1,2.12.65l.9-1.268a.5.5,0,0,1,.7-.117l1.318.934a.5.5,0,0,1,.121.693l-.877,1.233a7.948,7.948,0,0,1,2.462,5.758,8,8,0,0,1-8,7.983A8,8,0,0,1,1238-12037.673Zm1.92,0a6.078,6.078,0,0,0,6.078,6.066,6.081,6.081,0,0,0,6.081-6.066,6.083,6.083,0,0,0-6.081-6.069A6.081,6.081,0,0,0,1239.922-12037.673Zm6.039-.042v-4.96l.039,0a5.016,5.016,0,0,1,5.014,4.963Z"
                                    transform="translate(-6890 17687.379)"
                                  />
                                </svg>
                              </ClockIcon>
                              {formatTimeInterval(
                                this.props.timeUntilNextLockToggle
                              )}
                            </Clock>
                            <Media
                              query="(max-width: 479px), (min-width: 640px)"
                              render={() => (
                                <ClockText>
                                  <Gettext>
                                    left till the market closure
                                  </Gettext>
                                </ClockText>
                              )}
                            />
                          </ClockContainer>
                        )}
                      </SettingsContainer>
                    ) : (
                      <Tour
                        step="chartWithDeals"
                        originalRef={this.originalDeals}
                        withoutStyles={true}
                        withMobileArrow={true}
                        dealsContainer={true}
                      >
                        {({ next, isOriginal, isTourMode }) => (
                          <DealsContainer ref={this.originalDeals}>
                            <ModalConsumer>
                              {({ showModal }) =>
                                !isTourMode ? (
                                  <TradeHistory
                                    tradeMode={tradeMode}
                                    mobile={true}
                                    deals={deals}
                                    now={this.props.now}
                                    point={this.props.point}
                                    pointsByStock={this.props.pointsByStock}
                                    onOperationClick={
                                      isOriginal
                                        ? operation => {
                                            if (
                                              FeatureFlags.OperationCheque &&
                                              operation.profit != null
                                            ) {
                                              showModal(OperationCheque, {
                                                operation,
                                                type: tradeMode
                                              });
                                            } else {
                                              this.props.dispatch({
                                                type: "SWITCH_TO_STOCK",
                                                payload: operation.stock
                                              });
                                            }
                                          }
                                        : () => void 0
                                    }
                                    refundClickHandler={
                                      this.props.refundClickHandler
                                    }
                                    refundFetching={this.props.refundFetching}
                                  />
                                ) : (
                                  <div
                                    ref={this.originalDeals}
                                    style={{ pointerEvents: "none" }}
                                  >
                                    <ArtificialTourDeals mobile={true} />
                                  </div>
                                )
                              }
                            </ModalConsumer>
                          </DealsContainer>
                        )}
                      </Tour>
                    )}
                    <Tour step="makeDeal" originalRef={this.original}>
                      {({ next, isOriginal, tour }) => (
                        <ButtonContainer
                          tourBorder={!isOriginal && tour && tour.borderStyles}
                          tourBorderAnimation={
                            !isOriginal &&
                            tour &&
                            tour.outside &&
                            tour.borderStyles
                          }
                          isLandscape={true}
                          deals={this.state.activeTab === "deals"}
                          ref={isOriginal ? this.original : null}
                          disabled={disabled}
                        >
                          <ButtonWrapper>
                            <CastUp
                              onClick={() => {
                                if (isOriginal) {
                                  this._submit("UP");
                                } else {
                                  next(["UP", currentBid, tourInterval]);
                                }
                              }}
                            />
                          </ButtonWrapper>
                          <ButtonWrapper>
                            <CastDown
                              onClick={() => {
                                if (isOriginal) {
                                  this._submit("DOWN");
                                } else {
                                  next(["DOWN", currentBid, tourInterval]);
                                }
                              }}
                            />
                          </ButtonWrapper>
                        </ButtonContainer>
                      )}
                    </Tour>
                  </Content>
                </Container>
              )}
            </Orientation>
          )
        }
      </Media>
    );
  }
}

export default withRouter(props => (
  <ActiveTournament>
    {active_tournament => (
      <TradeControlTabs
        {...props}
        active_tournament={active_tournament}
        ref={props.controlsRef}
      />
    )}
  </ActiveTournament>
));



// WEBPACK FOOTER //
// ./src/components/TradeMobile/TradeControlTabs.js